import { useState } from 'react';
import type { CrewSettingDto, StandingsDto } from '@shared/types';
import { errorMessage, useApp, useLoad } from '../context';
import { Card, Empty, ErrorBox, Loading, Pill, TopBar } from '../components/ui';

const fmt = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 2 });

/** Crews on the shop board: one per Connecteam direct manager. Admins hide managers who aren't foremen. */
export function Crews() {
  const { api, toast } = useApp();
  const crews = useLoad(() => api.get<CrewSettingDto[]>('/api/crews'), [api]);
  const board = useLoad(() => api.get<StandingsDto>('/api/standings?period=weekly'), [api]);
  const [open, setOpen] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const toggle = async (c: CrewSettingDto) => {
    setSaving(c.employeeId);
    try {
      await api.put(`/api/crews/${c.employeeId}`, { showOnBoard: !c.showOnBoard });
      toast(c.showOnBoard ? `${c.foremanName}'s crew hidden from the board` : `${c.foremanName}'s crew shown on the board`);
      crews.reload(); board.reload();
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setSaving(null); }
  };

  return (
    <div className="page">
      <TopBar title="Crews" back="/more" />
      <p className="muted small pad">Crews come from each person's Direct manager in Connecteam. A foreman's crew is the foreman plus their direct reports, ranked on the shop TV by percent of the crew's maximum weekly points. Hide anyone here who isn't a foreman, such as office managers.</p>
      {board.data && (
        <Card>
          <div className="card-kicker">This week ({board.data.label})</div>
          {board.data.maxPointsPerPerson
            ? <div className="muted small">Max {fmt(board.data.maxPointsPerPerson)} points per person. Change it under Rules, Weekly drawing type.</div>
            : <div className="warn small">No weekly maximum is set, so crews rank by average points. Set it under Rules, Weekly drawing type.</div>}
          {board.data.crews.length === 0 ? <Empty>No crews to show.</Empty> : board.data.crews.map((c) => (
            <div key={c.foremanName} className="list-row" style={{ cursor: 'default' }}>
              <div><strong>{c.rank}. {c.foremanName}</strong><div className="muted small">{c.members} people • {fmt(c.points)}{c.maxPoints > 0 ? ` of ${fmt(c.maxPoints)}` : ''} points</div></div>
              <strong className="tabular">{c.maxPoints > 0 ? `${fmt(c.percentOfMax)}%` : `${fmt(c.percentOfMax)} avg`}</strong>
            </div>
          ))}
        </Card>
      )}
      {crews.loading && !crews.data && <Loading />}
      {crews.error && <ErrorBox message={crews.error} onRetry={crews.reload} />}
      {crews.data && (
        <Card>
          <div className="card-kicker">Managers</div>
          {crews.data.length === 0 ? <Empty>No one has direct reports in Connecteam yet.</Empty> : crews.data.map((c) => (
            <div key={c.employeeId}>
              <div className="list-row" style={{ cursor: 'default' }}>
                <button className="list-row-main" onClick={() => setOpen(open === c.employeeId ? null : c.employeeId)}>
                  <div><strong>{c.foremanName}</strong> {c.showOnBoard ? <Pill kind="ok">On board</Pill> : <Pill kind="muted">Hidden</Pill>}
                    <div className="muted small">{c.directReports} direct report{c.directReports === 1 ? '' : 's'} • tap to see the crew</div></div>
                </button>
                <button className="btn btn-secondary btn-sm" disabled={saving !== null} onClick={() => void toggle(c)}>{saving === c.employeeId ? 'Saving…' : c.showOnBoard ? 'Hide' : 'Show'}</button>
              </div>
              {open === c.employeeId && <p className="muted small pad">{c.memberNames.join(', ')}</p>}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
